// services/chatBlockThreads.ts
//
// One companion chat for a whole QBank block (ORA-1285). A block thread is an
// ordinary conversation that the server has tied to a block; these two calls
// tie and untie it. Finding the thread is the anchor lookup
// (`GET /conversations/anchor?type=qbank_block&id=<blockId>`), and the first
// turn of a block with no thread sends `threadScope: 'qbank_block'`.
//
//   POST   /api/v5/chat/conversations/:id/block-thread  body { blockId }
//   DELETE /api/v5/chat/conversations/:id/block-thread
//   200 { conversation: <the GET /:id detail> } · both idempotent
//   404 { error: 'not_found', code: 'block_ineligible' | 'conversation_not_found' }
//
// `block_ineligible` (a self-assessment, a block that is not the student's)
// means the block gets no block chat. `conversation_not_found` refuses only
// the conversation: the block can still have one, started by the next turn. An
// older backend sends no code; that is read as the block being refused.
// Nothing here dispatches.

import axiosInstance from './axiosCreator';
import { isAxiosStatus, readApiErrorCode } from './apiError';
import {
  CHAT_CONVERSATIONS_ROUTE,
  parseChatConversationDetail,
  type ChatConversationDetail,
} from './chatConversations';

const blockThreadRoute = (conversationId: string): string =>
  `${CHAT_CONVERSATIONS_ROUTE}/${encodeURIComponent(conversationId)}/block-thread`;

const readConversation = (data: unknown): ChatConversationDetail | null =>
  typeof data === 'object' && data !== null
    ? parseChatConversationDetail(
        (data as Record<string, unknown>).conversation
      )
    : null;

/** What the server said to making a conversation a block's thread. */
export type ChatBlockThreadResult =
  | { ok: true; conversation: ChatConversationDetail }
  | { ok: false; reason: 'block_ineligible' | 'conversation_not_found' };

/** Make `conversationId` the thread of `blockId`. */
export const setChatBlockThread = async (
  conversationId: string,
  blockId: string
): Promise<ChatBlockThreadResult> => {
  try {
    const response = await axiosInstance.post(
      blockThreadRoute(conversationId),
      {
        blockId,
      }
    );

    const conversation = readConversation(response.data);

    return conversation
      ? { ok: true, conversation }
      : { ok: false, reason: 'conversation_not_found' };
  } catch (error) {
    if (!isAxiosStatus(error, 404)) throw error;

    return {
      ok: false,
      reason:
        readApiErrorCode(error) === 'conversation_not_found'
          ? 'conversation_not_found'
          : 'block_ineligible',
    };
  }
};

/**
 * Release a block thread: it stays a normal chat about the question it started
 * on. Resolves to the conversation as the server now has it, or null on a 404,
 * which means the conversation is not a block thread anyway.
 */
export const releaseChatBlockThread = async (
  conversationId: string
): Promise<ChatConversationDetail | null> => {
  try {
    const response = await axiosInstance.delete(
      blockThreadRoute(conversationId)
    );

    return readConversation(response.data);
  } catch (error) {
    if (isAxiosStatus(error, 404)) return null;
    throw error;
  }
};
